import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Field,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  Select,
} from "../../../design-system";
import { createSpecialSchedule, getWorkSchedules } from "../../../helpers/kronaly_helper";

const unwrap = (r) => {
  const inner = r?.data ?? r;
  return inner?.data ?? inner;
};

const WEEKDAYS = [
  { key: "mon", label: "Lun" },
  { key: "tue", label: "Mar" },
  { key: "wed", label: "Mié" },
  { key: "thu", label: "Jue" },
  { key: "fri", label: "Vie" },
  { key: "sat", label: "Sáb" },
  { key: "sun", label: "Dom" },
];

/**
 * Solicitud de jornada especial (p. ej. intensiva de verano). El horario puede
 * reutilizar uno del catálogo o definirse a medida (horas por día laborable).
 * Queda pendiente de aprobación por un manager.
 */
export default function SolicitarEspecialModal({ open, onClose, onCreated, tz, employeeProfileId = null }) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [note, setNote] = useState("");
  const [mode, setMode] = useState("catalog"); // catalog | custom

  const [schedules, setSchedules] = useState([]);
  const [workScheduleId, setWorkScheduleId] = useState("");

  // A medida: mismos horarios para los días marcados.
  const [days, setDays] = useState({ mon: true, tue: true, wed: true, thu: true, fri: true, sat: false, sun: false });
  const [start, setStart] = useState("08:00");
  const [end, setEnd] = useState("15:00");
  const [breakMin, setBreakMin] = useState(15);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    getWorkSchedules().then((r) => setSchedules(unwrap(r) || [])).catch(() => setSchedules([]));
  }, [open]);

  const canSubmit = useMemo(() => {
    if (!startDate || !endDate || endDate < startDate) return false;
    if (mode === "catalog") return !!workScheduleId;
    const anyDay = Object.values(days).some(Boolean);
    return anyDay && !!start && !!end && end > start;
  }, [startDate, endDate, mode, workScheduleId, days, start, end]);

  // Total diario a medida = (salida − entrada) − pausa.
  const dailyMin = useMemo(() => {
    const toMin = (hhmm) => { const [h, m] = String(hhmm || "").split(":").map(Number); return (h || 0) * 60 + (m || 0); };
    const net = toMin(end) - toMin(start) - (Number(breakMin) || 0);
    return net > 0 ? net : 0;
  }, [start, end, breakMin]);
  const selectedDays = Object.values(days).filter(Boolean).length;
  const fmtHM = (min) => `${Math.floor(min / 60)}h${min % 60 ? ` ${min % 60}m` : ""}`;

  const buildPayload = () => {
    const base = { start_date: startDate, end_date: endDate, note: note.trim() || null };
    if (employeeProfileId) base.employee_profile_id = employeeProfileId;
    if (mode === "catalog") {
      return { ...base, source: "catalog", work_schedule_id: workScheduleId };
    }
    const daysConfig = {};
    WEEKDAYS.forEach(({ key }) => {
      if (days[key]) {
        daysConfig[key] = { start, end, break: { minutes: Number(breakMin) || 0 } };
      }
    });
    return { ...base, source: "custom", config: { timezone: tz || "Europe/Madrid", days: daysConfig } };
  };

  const submit = async () => {
    if (!canSubmit || saving) return;
    setSaving(true);
    setError(null);
    try {
      await createSpecialSchedule(buildPayload());
      onCreated?.();
    } catch (e) {
      setError(e?.response?.data?.error?.message || e?.response?.data?.message || e?.message || "No se pudo asignar la jornada especial.");
      setSaving(false);
    }
  };

  if (!open) return null;

  return (
    <Modal open={open} onClose={onClose} title="Asignar jornada especial" size="md">
      <ModalBody>
        {error && <Alert tone="danger" style={{ marginBottom: 14 }}>{error}</Alert>}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
          <Field label="Desde" required>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </Field>
          <Field label="Hasta" required>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>

        <Field label="Horario" style={{ marginTop: 6 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <ModeBtn active={mode === "catalog"} onClick={() => setMode("catalog")}>Del catálogo</ModeBtn>
            <ModeBtn active={mode === "custom"} onClick={() => setMode("custom")}>A medida</ModeBtn>
          </div>

          {mode === "catalog" ? (
            <Select value={workScheduleId} onChange={(e) => setWorkScheduleId(e.target.value)}>
              <option value="">Selecciona un horario…</option>
              {schedules.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}{s.weekly_minutes ? ` · ${Math.round(s.weekly_minutes / 60)}h/sem` : ""}
                </option>
              ))}
            </Select>
          ) : (
            <div style={{ border: "1px solid var(--kr-border-subtle)", borderRadius: 8, padding: 12 }}>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 12 }}>
                {WEEKDAYS.map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setDays((d) => ({ ...d, [key]: !d[key] }))}
                    style={{
                      width: 40, height: 32, borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: "pointer",
                      border: `1px solid ${days[key] ? "var(--kr-action)" : "var(--kr-border)"}`,
                      background: days[key] ? "var(--kr-action-soft-bg)" : "#fff",
                      color: days[key] ? "var(--kr-action-soft-fg)" : "var(--kr-fg-muted)",
                    }}
                  >{label}</button>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
                <Field label="Entrada"><Input type="time" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
                <Field label="Salida"><Input type="time" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
                <Field label="Pausa (min)"><Input type="number" min={0} value={breakMin} onChange={(e) => setBreakMin(e.target.value)} /></Field>
              </div>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginTop: 10, padding: "8px 10px", background: "var(--kr-neutral-50)", borderRadius: 6 }}>
                <span style={{ fontSize: 12, color: "var(--kr-fg-muted)" }}>Total por día</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: dailyMin > 0 ? "var(--kr-fg-strong)" : "var(--kr-danger-700)", fontFamily: "var(--kr-font-mono)" }}>
                  {dailyMin > 0 ? `${fmtHM(dailyMin)}/día` : "revisa las horas"}
                  {dailyMin > 0 && selectedDays ? ` · ${fmtHM(dailyMin * selectedDays)}/semana` : ""}
                </span>
              </div>
              <p style={{ margin: "8px 0 0", fontSize: 12, color: "var(--kr-fg-muted)" }}>
                Se aplica a los días marcados. La pausa se descuenta del cómputo (mínimo legal 15 min).
              </p>
            </div>
          )}
        </Field>

        <Field label="Motivo (opcional)" style={{ marginTop: 6 }}>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Ej. Jornada intensiva de verano" />
        </Field>
      </ModalBody>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose}>Cancelar</Button>
        <Button variant="primary" icon="CalendarCheck" disabled={!canSubmit} onClick={submit}>
          {saving ? "Asignando…" : "Asignar"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

function ModeBtn({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        height: 34, padding: "0 14px", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer",
        border: `1px solid ${active ? "var(--kr-fg-strong)" : "var(--kr-border)"}`,
        background: active ? "var(--kr-fg-strong)" : "#fff",
        color: active ? "#fff" : "var(--kr-fg-primary)",
      }}
    >{children}</button>
  );
}
